/**
 * LocationPickerModal - Modal for selecting event location on a map
 * Issue #1081: Location picker for events
 *
 * Features:
 * - Search box with Nominatim geocoding
 * - Interactive map with draggable pin
 * - "Use Current Location" button
 * - Confirmation flow for updating location name
 */

import { useState, useEffect, useRef } from 'react';
import { X, Search, MapPin, Navigation } from 'lucide-react';
import { OroLoader } from './OroLoader';
import { toast } from 'sonner';
import Button from './Button';
import LocationPickerMap from './LocationPickerMap';

export interface LocationPickerResult {
  lat: number;
  lng: number;
  name?: string;
  address?: string;
}

interface LocationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: LocationPickerResult) => void;
  /** Current location (if editing) */
  initialLocation?: { lat?: number; lng?: number };
  /** Current location name (for update confirmation) */
  currentLocationName?: string;
}

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  name?: string;
  address?: {
    amenity?: string;
    road?: string;
    city?: string;
    state?: string;
    country?: string;
  };
}

export default function LocationPickerModal({
  isOpen,
  onClose,
  onConfirm,
  initialLocation,
  currentLocationName,
}: LocationPickerModalProps) {
  // Location state
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [locationName, setLocationName] = useState<string>('');
  const [locationAddress, setLocationAddress] = useState<string>('');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Geocoding state
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);

  // Current location state
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Confirmation state
  const [showNameConfirm, setShowNameConfirm] = useState(false);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialLocation?.lat && initialLocation?.lng) {
        setSelectedLocation({ lat: initialLocation.lat, lng: initialLocation.lng });
      } else {
        setSelectedLocation(null);
      }
      setLocationName('');
      setLocationAddress('');
      setSearchQuery('');
      setSearchResults([]);
      setShowResults(false);
      setShowNameConfirm(false);
    }
  }, [isOpen, initialLocation?.lat, initialLocation?.lng]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showNameConfirm) {
          setShowNameConfirm(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, showNameConfirm]);

  // Search with Nominatim
  const handleSearch = async (query: string) => {
    if (query.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
        { headers: { 'User-Agent': 'ORO-Move/1.0 (https://oro.fit)' } }
      );
      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
        setShowResults(true);
      }
    } catch {
      toast.error('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(value);
    }, 400);
  };

  // Select search result
  const handleSelectResult = (result: NominatimResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setSelectedLocation({ lat, lng });
    setLocationName(result.name || result.address?.amenity || result.address?.road || '');
    setLocationAddress(result.display_name);
    setSearchQuery('');
    setShowResults(false);
  };

  // Reverse geocode when location changes (from map click/drag)
  const handleLocationChange = async (location: { lat: number; lng: number }) => {
    setSelectedLocation(location);
    setIsReverseGeocoding(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${location.lat}&lon=${location.lng}&format=json&addressdetails=1`,
        { headers: { 'User-Agent': 'ORO-Move/1.0 (https://oro.fit)' } }
      );
      if (response.ok) {
        const data = await response.json();
        setLocationName(
          data.name || data.address?.amenity || data.address?.road || ''
        );
        setLocationAddress(data.display_name || '');
      }
    } catch {
      // Geocoding failed silently - user can still use coordinates
    } finally {
      setIsReverseGeocoding(false);
    }
  };

  // Get current location
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        handleLocationChange({ lat: latitude, lng: longitude });
        setIsGettingLocation(false);
      },
      (error) => {
        setIsGettingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error('Location access denied. Please enable location permissions.');
        } else {
          toast.error('Failed to get current location');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Handle confirm button
  const handleConfirmClick = () => {
    if (!selectedLocation) return;

    // If there's an existing location name and we have a new one, ask to update
    if (currentLocationName && locationName && currentLocationName !== locationName) {
      setShowNameConfirm(true);
    } else {
      // No conflict, just confirm
      onConfirm({
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        name: locationName || undefined,
        address: locationAddress || undefined,
      });
      onClose();
    }
  };

  // Confirm with keeping current name
  const handleKeepCurrentName = () => {
    if (!selectedLocation) return;
    onConfirm({
      lat: selectedLocation.lat,
      lng: selectedLocation.lng,
      // Don't include name/address - keep existing
    });
    onClose();
  };

  // Confirm with new name
  const handleUseNewName = () => {
    if (!selectedLocation) return;
    onConfirm({
      lat: selectedLocation.lat,
      lng: selectedLocation.lng,
      name: locationName || undefined,
      address: locationAddress || undefined,
    });
    onClose();
  };

  if (!isOpen) return null;

  // Name confirmation dialog
  if (showNameConfirm) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60" onClick={() => setShowNameConfirm(false)} />
        <div className="relative bg-oro-surface border border-oro-border rounded-xl w-full max-w-sm shadow-card">
          <div className="p-4 border-b border-oro-border">
            <h2 className="font-semibold text-text-primary">Update Location Name?</h2>
          </div>
          <div className="p-4 space-y-3">
            <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-3">
              <p className="text-xs text-text-muted mb-1">Current</p>
              <p className="text-text-primary font-medium">{currentLocationName}</p>
            </div>
            <div className="bg-accent/10 border border-accent/30 rounded-lg p-3">
              <p className="text-xs text-text-muted mb-1">New</p>
              <p className="text-accent font-medium">{locationName}</p>
            </div>
          </div>
          <div className="p-4 border-t border-oro-border grid grid-cols-2 gap-3">
            <Button variant="secondary" onClick={handleKeepCurrentName}>
              Keep Current
            </Button>
            <Button variant="primary" onClick={handleUseNewName}>
              Use New Name
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-oro-surface border border-oro-border rounded-xl w-full max-w-lg shadow-card max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-oro-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
              <MapPin className="w-5 h-5 text-accent" />
            </div>
            <h2 className="font-semibold text-text-primary">Choose Location</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-muted hover:text-text-secondary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                placeholder="Search for a place..."
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg pl-10 pr-10 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
              />
              {isSearching && (
                <OroLoader variant="bounce" size={16} className="absolute right-3 top-1/2 -translate-y-1/2" label="Searching" />
              )}
            </div>

            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-oro-surface border border-oro-border rounded-lg shadow-card max-h-48 overflow-y-auto">
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectResult(result)}
                    className="w-full px-4 py-3 text-left hover:bg-oro-surface-alt border-b border-oro-border last:border-b-0 transition-colors"
                  >
                    <p className="text-text-primary text-sm font-medium truncate">
                      {result.name || result.address?.road || 'Unknown'}
                    </p>
                    <p className="text-text-muted text-xs truncate">{result.display_name}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Map */}
          <LocationPickerMap
            location={selectedLocation}
            onLocationChange={handleLocationChange}
            height="250px"
          />

          {/* Selected Location Display */}
          {selectedLocation && (
            <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-3">
              {isReverseGeocoding ? (
                <div className="flex items-center gap-2 text-text-muted">
                  <OroLoader variant="inline" decorative />
                  <span className="text-sm">Getting address...</span>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-text-primary font-medium">
                        {locationName || 'Selected Location'}
                      </p>
                      {locationAddress && (
                        <p className="text-text-muted text-sm truncate">{locationAddress}</p>
                      )}
                      <p className="text-text-muted text-xs mt-1">
                        {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Use Current Location */}
          <Button
            variant="secondary"
            onClick={handleUseCurrentLocation}
            disabled={isGettingLocation}
            className="w-full"
          >
            {isGettingLocation ? (
              <>
                <OroLoader variant="inline" className="mr-2" decorative />
                Getting location...
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4 mr-2" />
                Use Current Location
              </>
            )}
          </Button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-oro-border shrink-0 grid grid-cols-2 gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmClick}
            disabled={!selectedLocation || isReverseGeocoding}
          >
            Confirm Location
          </Button>
        </div>
      </div>
    </div>
  );
}
